import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Sidebar } from '@/widgets/Sidebar';
import { Header } from '@/widgets/Header';
import { PaymentReminder } from '@/features/PaymentReminder';
import { BadgeCelebration } from '@/features/Badges';
import { useNotificationsRealtime } from '@/features/Chat';
import { getMyProfile, isProfileStudent, useGetMyProfileQuery } from '@/entities/Profile';
import { getIsAuth } from '@/entities/Auth';
import { LocalStorageService } from '@/shared/services/localStorage.service';
import { Container } from '@/shared/ui/Container';
import { SectionLoader } from '@/shared/ui/Loader';
import { PageTitleContext } from '@/shared/lib/hooks';

export type PageMeta = {
  title?: string;
  description?: string;
};

interface MainLayoutProps {
  children: ReactNode;
  meta?: PageMeta;
  headerTitle?: string;
  headerDescription?: string;
  headerInputPlaceholder?: string;
  headerButtonText?: string;
  headerOnPress?: () => void;
  headerGoBackButton?: boolean;
  onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  searchValue?: string;
  actionsDisabled?: boolean;
  onUserActions?: () => void;
  onSettings?: () => void;
  onLogout?: () => void;
}

export const MainLayout = ({
  children,
  meta,
  onUserActions,
  onSettings,
  onLogout,
}: MainLayoutProps) => {
  const { t } = useTranslation();
  const isAuth = useSelector(getIsAuth);
  const user = useSelector(getMyProfile);

  // Вложенный экран сообщает своё название сюда: у него нет своего макета,
  // а заголовок раздела его не описывает. Пока не сообщил — берём meta.
  const [nestedTitle, setNestedTitle] = useState<string>();
  const publishTitle = useCallback((title?: string) => setNestedTitle(title), []);
  const pageTitle = nestedTitle ?? meta?.title;
  const isStudent = Boolean(user && isProfileStudent(user));
  const accessToken = LocalStorageService.getAccessTokenFromAnyStorage();

  // Тост об уведомлении должен всплывать на любом экране, поэтому подписка живёт здесь:
  // макет один на приложение, а экран списка уведомлений — нет.
  useNotificationsRealtime();

  // Загружаем профиль пользователя
  const { isLoading: isProfileLoading } = useGetMyProfileQuery(undefined, {
    skip: !isAuth && !accessToken,
  });

  useEffect(() => {
    document.title = meta?.title ? `${meta.title} | IHCL LMS` : 'IHCL LMS';
    if (meta?.description) {
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', meta.description);
      } else {
        const metaTag = document.createElement('meta');
        metaTag.name = 'description';
        metaTag.content = meta.description;
        document.head.appendChild(metaTag);
      }
    }
  }, [meta]);

  return (
    <Container>
      {/*
        Первый элемент в табуляции: с клавиатуры без него приходится проходить всю шапку
        и меню на каждой странице. Виден только при фокусе, но виден — поэтому оформлен
        как кнопка сайта, а не служебной заплаткой.
      */}
      <a
        href="#main-content"
        className="sr-only rounded-xl focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:flex focus:h-11 focus:items-center focus:bg-red focus:px-5 focus:text-base focus:font-medium focus:text-white"
      >
        {t('layout.skipToMain')}
      </a>
      <div className="min-h-dvh flex flex-col gap-6 py-6">
        <Header onUserActions={onUserActions} onSettings={onSettings} onLogout={onLogout} />
        <div className="flex-1 min-h-0 w-full flex gap-6 relative">
          <Sidebar />

          <main id="main-content" tabIndex={-1} className="flex-1 min-h-0 min-w-0 flex flex-col">
            {/*
              Заголовок первого уровня на каждой странице. Скрыт визуально: в макете
              отдельной строки под него нет, а для скринридера страница без h1 —
              страница без названия.
            */}
            {pageTitle && <h1 className="sr-only">{pageTitle}</h1>}
            {isProfileLoading ? (
              <SectionLoader className="flex items-center justify-center min-h-[400px]" />
            ) : (
              <PageTitleContext.Provider value={publishTitle}>{children}</PageTitleContext.Provider>
            )}
          </main>
        </div>
      </div>

      {/* Счета и бейджи есть только у студентов. Оба окна сами решают, показываться ли. */}
      {isStudent && <PaymentReminder />}
      {isStudent && <BadgeCelebration />}
    </Container>
  );
};
